import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./EngagementFormConfig.module.css";
import { Row } from "react-bootstrap";
import CygnusForm from "../../../Reusables/components/CygnusForm/CygnusForm";
import CygnusFormBuilder from "../../../Reusables/components/CygnusFormBuilder/CygnusFormBuilder";
import {
  InputStructureProps,
  createDefaultInitialData,
} from "../../../Reusables/components/CygnusFormBuilder/CygnusFormBuilder.utils";
import {
  fetchEngagementLetterTemplateFields,
  saveFormData,
  fetchEngagementLetterTemplates,
} from "./EngagementFormConfig.service";
import { FieldProps } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import { engagementMetaData } from "./EngagementFormConfig.utils";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import { optionsConverter } from "../../../pages/client_onboarding/OnboardingFormConfig/OnboardingFormConfig.utils";

/**
 * `EngagementFormConfig` manages metadata (name, purpose, template, type),
 * form building with CygnusFormBuilder, and previewing via CygnusForm.
 *
 * @returns {JSX.Element}
 */
const EngagementFormConfig: React.FC<{
  engagement_form_config_option: {
    clientType: string;
    clientTypeCode: string;
    createform_option: {
      id: string;
      templateid: string;
      form_name: string;
      is_final: string;
      form_purpose: string;
      initial_data: InputStructureProps;
      preview_data: FieldProps[];
    };
  };
}> = (config_props) => {
  const configOption = useMemo(
    () => config_props?.engagement_form_config_option || {},
    [config_props]
  );
  const prefill = useMemo(
    () => configOption?.createform_option || {},
    [configOption]
  );
  const [initialData, setInitialData] = useState<InputStructureProps>(
    createDefaultInitialData()
  );
  const [elFieldOptions, setElFieldOptions] = useState<
    {
      value: string;
      options: [];
      label: string;
      type: string;
      id: string;
    }[]
  >([]);
  const [metadataFields, setMetadataFields] =
    useState<FieldProps[]>(engagementMetaData);
  const [isMetadataValid, setIsMetadataValid] = useState(false);
  const [update, setUpdate] = useState(false);
  const hasPrefilledRef = useRef(false);
  const isPrefillingRef = useRef(false);
  
  useEffect(() => {
    const canPrefill =
      prefill && metadataFields.length > 0 && !hasPrefilledRef.current;
    if (!canPrefill) return;
    hasPrefilledRef.current = true;
    isPrefillingRef.current = true;

    setMetadataFields((prevFields) =>
      prevFields.map((field) => {
        switch (field.id) {
          case "formName":
            return { ...field, value: prefill.form_name };
          case "engagementLetterTemplate":
            return { ...field, value: prefill.templateid };
          case "isFinal":
            return {
              ...field,
              value: prefill.is_final ?? false,
              label: "Draft",
            };
          default:
            return field;
        }
      })
    );

    // Set the initial form layout and preview if available
    if (prefill.initial_data) {
      setInitialData(prefill.initial_data);
      setUpdate(true);
    }
    setTimeout(() => {
      isPrefillingRef.current = false;
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps --- other dependencies not needed here
  }, [prefill]);

  // Dynamically update dependent fields
  const handleMetadataChange = (updatedFields: FieldProps[]) => {
    if (isPrefillingRef.current) return;
    // Find isFinal field and update its label dynamically
    const finalField = updatedFields.find((f) => f.id === "isFinal");
    if (finalField) {
      finalField.label = finalField.value ? "Final" : "Draft";
    }
    const prevTemplateValue = metadataFields.find(
      (f) => f.id === "engagementLetterTemplate"
    )?.value;
    const newTemplateValue = updatedFields.find(
      (f) => f.id === "engagementLetterTemplate"
    )?.value;
    // Only fetch if the template value has changed
    if (newTemplateValue && newTemplateValue !== prevTemplateValue) {
      loadTemplateFields(newTemplateValue);
    }

    setMetadataFields([...updatedFields]);
  };

  useEffect(() => {
    fetchEngagementLetterTemplates().then((els) => {
      const engalementLetters = els.map((m: any) => ({
        label: m.TemplateOriginalFileName,
        value: m.TemplateId,
      }));

      // Set client types into metadata
      setMetadataFields((prev) =>
        prev.map((field) =>
          field.id === "engagementLetterTemplate" 
            ? {
                ...field,
                options: engalementLetters,
                value: field.value || null,
              }
            : field
        )
      );
    });
    loadTemplateFields();
  }, []);

  const loadTemplateFields = async (updatedTemplateId?: number) => {
    var templateId = updatedTemplateId ?? Number(config_props?.engagement_form_config_option?.createform_option?.templateid);
    const loadFields = async () => {
      const rawFields = await fetchEngagementLetterTemplateFields(
        templateId
      );
      const parsedFields = await optionsConverter(rawFields);
      setElFieldOptions(parsedFields);
    };
    loadFields();
  }

  const handleSave = async () => {
    const fieldMap: Record<string, any> = {};
    metadataFields.forEach((f) => (fieldMap[f.id] = f.value));
    const payload = update
      ? {
          ELFormId: prefill.id,
          FormName: fieldMap["formName"],
          TemplateId: fieldMap["engagementLetterTemplate"],
          Status: fieldMap["isFinal"] ? "Active" : "Inactive",
          InputStructure: initialData,
          Active: "Y",
        }
      : {
          FormName: fieldMap["formName"],
          TemplateId: fieldMap["engagementLetterTemplate"],
          Status: fieldMap["isFinal"] ? "Active" : "Inactive",
          InputStructure: initialData,
          Active: "Y",
        };

    await saveFormData(payload);
  };

  return (
    <>
      <Row className={`no-gutter ${styles.formMetadataWrapper}`}>
        <p className="display-6"> Cygnus Engagement Form Builder</p>
      </Row>
      <Row className={`no-gutter ${styles.formMetadataWrapper}`}>
        <div className={styles.formMetadataContainer}>
          <CygnusForm
            formId="cygnus-engagement-form"
            fields={metadataFields}
            onFieldsChange={(fields) => handleMetadataChange(fields)}
            onValidationChange={(flag) => {
              setIsMetadataValid(flag);
            }}
          />
        </div>
        <div className={styles.onboardingFormSaveWrapper}>
          <CustomButton
            disabled={!isMetadataValid}
            onclick={handleSave}
            content={"Save"}
          />
        </div>
      </Row>
      <Row className="no-gutters">
        <CygnusFormBuilder
          initialData={initialData}
          updateInitialData={setInitialData}
          fieldOptions={elFieldOptions}
          templateStatus={
            metadataFields.find((f) => f.id === "isFinal")?.value
              ? "Final"
              : "Draft"
          }
        />
      </Row>
    </>
  );
};

export default EngagementFormConfig;
